import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Visibility,
  VisibilityOff,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  InputAdornment,
  IconButton,
} from '@mui/material';

import { useAuth } from '../contexts/AuthContext';
import Logo from '../components/common/Logo';

export default function Unlock() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, unlock, logout } = useAuth();

  const [masterPassword, setMasterPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Get the intended destination after unlock
  const from = (location.state as { from?: Location })?.from?.pathname || '/vault';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!masterPassword) {
      setError('Please enter your master password');
      return;
    }

    setLoading(true);

    try {
      await unlock(masterPassword);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to unlock vault');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        bgcolor: 'background.default',
        py: 4,
      }}
    >
      <Container maxWidth="xs">
        <Paper elevation={0} sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mb: 2,
              }}
            >
              <Logo size="large" showWordmark={false} />
            </Box>
            
            <Typography variant="h5" fontWeight={600} gutterBottom>
              Vault Locked
            </Typography>
            
            <Typography color="text.secondary" variant="body2">
              Your session is active but the vault is locked.
            </Typography>
            
            {user && (
              <Typography
                color="primary"
                variant="body2"
                fontWeight={500}
                sx={{ mt: 1 }}
              >
                {user.email}
              </Typography>
            )}
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Master Password"
              type={showPassword ? 'text' : 'password'}
              value={masterPassword}
              onChange={(e) => setMasterPassword(e.target.value)}
              margin="normal"
              autoComplete="current-password"
              autoFocus
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              fullWidth
              type="submit"
              variant="contained"
              size="large"
              disabled={loading}
              sx={{ mt: 3, mb: 2 }}
            >
              {loading ? 'Unlocking...' : 'Unlock Vault'}
            </Button>

            <Button
              fullWidth
              variant="text"
              color="inherit"
              startIcon={<LogoutIcon />}
              onClick={handleLogout}
              sx={{ color: 'text.secondary' }}
            >
              Log out and use a different account
            </Button>
          </form>
        </Paper>
      </Container>
    </Box>
  );
}

